import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../hooks/useAuth';

const AdminSettingsPage = () => {
  const { user, fetchProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ old: '', new: '', confirm: '' });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await fetchProfile();
        setProfileData(profile);
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };

    loadProfile();
  }, [fetchProfile]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement password change API call
    console.log('Change password:', passwordForm);
    setPasswordForm({ old: '', new: '', confirm: '' });
    setShowPasswordForm(false);
  };

  return (
    <AdminLayout title="Settings">
      <div className="max-w-2xl space-y-5">
        {/* Admin Profile Info */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-6">
          <h3 className="text-[13px] font-semibold text-slate-700 mb-4">Your Profile</h3>
          {profileData ? (
            <div className="space-y-4">
              {[
                { label: 'Name', value: profileData.name || user?.name },
                { label: 'Email', value: profileData.email || user?.email },
                { label: 'Role', value: profileData.role || user?.role },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                >
                  <span className="text-[12px] text-slate-500">{label}</span>
                  <span className="text-[13px] font-medium text-slate-800 capitalize">{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-slate-400">Loading profile…</p>
          )}
        </div>

        {/* Security - Change Password */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-6">
          <h3 className="text-[13px] font-semibold text-slate-700 mb-4">Security</h3>

          {!showPasswordForm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-medium text-slate-700">Password</p>
                <p className="text-[12px] text-slate-400 mt-0.5">Last changed: Never</p>
              </div>
              <button
                onClick={() => setShowPasswordForm(true)}
                className="px-4 py-1.5 text-[12.5px] font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              >
                Change Password
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-2">Current Password</label>
                <input
                  type="password"
                  name="old"
                  value={passwordForm.old}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-2">New Password</label>
                <input
                  type="password"
                  name="new"
                  value={passwordForm.new}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-2">Confirm Password</label>
                <input
                  type="password"
                  name="confirm"
                  value={passwordForm.confirm}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white text-[13px] font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 text-[13px] font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Platform Settings */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-6">
          <h3 className="text-[13px] font-semibold text-slate-700 mb-4">Platform Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-[13px] font-medium text-slate-700">Email Notifications</p>
                <p className="text-[12px] text-slate-500 mt-0.5">Receive updates about flagged content</p>
              </div>
              <label className="flex items-center">
                <input type="checkbox" className="rounded" defaultChecked />
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div>
                <p className="text-[13px] font-medium text-slate-700">Activity Logs</p>
                <p className="text-[12px] text-slate-500 mt-0.5">Track all admin actions</p>
              </div>
              <label className="flex items-center">
                <input type="checkbox" className="rounded" defaultChecked />
              </label>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-[13px] font-medium text-slate-700">Two-Factor Authentication</p>
                <p className="text-[12px] text-slate-500 mt-0.5">Enhance account security</p>
              </div>
              <button className="text-blue-600 text-[12px] font-medium hover:text-blue-700">
                Enable
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
